import { QueryHookOptions } from '@apollo/client';
import React from 'react';
import {
  FragmentData,
  OperationData,
  OperationVariables,
  gql,
} from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { getTaskStatus } from '../globals';
import {
  useTenantTimezoneConversion,
  utcToTenantToLocalDateFunction,
} from '../hooks/use-tenant-timezone';
import { TaskStatusEnum } from '../components/work/types/task-status-enum';

export const fragment = gql`
  fragment GetTask_Fragment on Task {
    cloudId
    jobId
    entityId
    taskTypeId
    name
    description
    assignedToUserId
    status
    dueDate
    completedDate
    wfDeletedOnUTC
    estimateMinutes
  }
` as import('../../__generated__/ts-gql/GetTask_Fragment').type;

export const GET_TASK_QUERY = gql`
  query GetTask($cloudId: String!) {
    getTask(cloudId: $cloudId) {
      ...GetTask_Fragment
    }
  }
` as import('../../__generated__/ts-gql/GetTask').type;

export type GetTaskQueryVariables = OperationVariables<typeof GET_TASK_QUERY>;
export type GetTaskQueryResult = OperationData<typeof GET_TASK_QUERY>;
export type GetTaskType = FragmentData<typeof fragment>;
export type GetTaskItem = NonNullable<ReturnType<typeof processData>>;

export const useGetTask = (
  options: QueryHookOptions<GetTaskQueryResult, GetTaskQueryVariables>
) => {
  const variables = options.variables as GetTaskQueryVariables;

  const res = useQuery(GET_TASK_QUERY, {
    context: { service: 'core' },
    variables,
    ...options,
  });

  const { utcToTenantToLocalDate, loading } = useTenantTimezoneConversion();

  return React.useMemo(() => {
    if (loading) {
      return { loading: true, data: undefined, error: undefined };
    }
    return {
      ...res,
      data: processData(res.data, utcToTenantToLocalDate),
    };
  }, [res, utcToTenantToLocalDate, loading]);
};

// export this so that if we ever need to refetch, we can process the data exactly the same
// otherwise, refetch returns the raw results, not a nice processed bunch of results
export function processData(
  data: GetTaskQueryResult | undefined,
  dateConvert: utcToTenantToLocalDateFunction
) {
  const task = data?.getTask;
  const result = task && {
    ...task,
    status: task.status
      ? getTaskStatus(task.status)
      : TaskStatusEnum.valuesEnum.NotStarted,
    dueDate: dateConvert(task.dueDate),
    // this is a mandatory field, we have retrieved it, we know it will never be empty
    taskTypeId: task.taskTypeId!,
    dueDateRaw: task.dueDate,
  };
  return result;
}
